import { useState, useCallback, useMemo } from 'react';
import { debounce } from 'throttle-debounce';

export const useDebouncedValue = <T>(
  initialValue: T,
  debounceDuration: number,
): DebouncedValue<T> => {
  const [value, setValue] = useState<T>(initialValue);
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  const debouncedSetter = useMemo(
    () =>
      debounce(debounceDuration, (newVal: T) => {
        setDebouncedValue(newVal);
      }),
    [debounceDuration],
  );

  const updateValues = useCallback(
    (val: T) => {
      setValue(val);
      debouncedSetter(val);
    },
    [debouncedSetter],
  );

  return [value, debouncedValue, updateValues];
};

type DebouncedValue<T> = [T, T, (val: T) => void];
